import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaHeadset, FaPaperPlane, FaRobot, FaTimes } from "react-icons/fa";
import { Link } from "react-router-dom";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import {
  closeSupportChat,
  loadSupportChatSettings,
  loadSupportThread,
  requestSupportHuman,
  sendSupportMessage,
  supportStaffContext,
} from "../Utils/supportChatApi";

const SUGGESTIONS = [
  "Today's attendance and collection?",
  "Fee collection this month?",
  "Find student by name",
  "Class strength list",
  "Exam list this session",
];

function bubbleColor(role) {
  if (role === "school") return { bg: COLORS.primary_color_shade7, color: "#fff", border: "none" };
  if (role === "crm") return { bg: "#E8F5E9", color: "#1B5E20", border: "1px solid #C8E6C9" };
  return { bg: "#fff", color: "#263238", border: "1px solid #E1F5FE" };
}

export default function AiAssistant() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState({
    service_enabled: true,
    ai_enabled: false,
  });
  const [serviceOff, setServiceOff] = useState(false);
  const pollRef = useRef(null);
  const listRef = useRef(null);

  const visible = useMemo(() => messages.slice(-40), [messages]);
  const mode = String(chat?.mode || (settings.ai_enabled ? "AI" : "HUMAN")).toUpperCase();
  const serviceOn = settings.service_enabled !== false && !serviceOff;

  const applyThread = useCallback((data) => {
    if (!data) return;
    if (data.settings) {
      setSettings({
        service_enabled: data.settings.service_enabled !== false,
        ai_enabled: !!data.settings.ai_enabled,
      });
    }
    if (data.api_status !== "OK") {
      if (/turned off/i.test(String(data.api_remark || ""))) {
        setServiceOff(true);
      }
      return;
    }
    setServiceOff(false);
    setChat(data.chat || null);
    setMessages(Array.isArray(data.messages) ? data.messages : []);
  }, []);

  const refresh = useCallback(async () => {
    const ctx = supportStaffContext();
    if (!ctx.school_code || !ctx.staff_id) return;
    try {
      const data = await loadSupportThread();
      applyThread(data);
    } catch (error) {
      console.error(error);
    }
  }, [applyThread]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await loadSupportChatSettings();
      if (cancelled || data.api_status !== "OK") return;
      setSettings({
        service_enabled: data.settings?.service_enabled !== false,
        ai_enabled: !!data.settings?.ai_enabled,
      });
      setServiceOff(data.settings?.service_enabled === false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open || !serviceOn) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    refresh();
    pollRef.current = setInterval(refresh, 8000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [open, refresh, serviceOn]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [visible, busy, open]);

  const ask = async (preset) => {
    const question = String(preset != null ? preset : draft).trim();
    if (!question || busy) return;
    const ctx = supportStaffContext();
    if (!ctx.school_code) {
      myToast.notify_warning("School code is missing. Please login again.");
      return;
    }
    if (!ctx.staff_id) {
      myToast.notify_warning("Staff session is missing. Please login again.");
      return;
    }
    if (!ctx.session_id && mode === "AI") {
      myToast.notify_warning("Open the dashboard once so the session is loaded, then ask again.");
      return;
    }
    setDraft("");
    setBusy(true);
    try {
      const data = await sendSupportMessage(question);
      if (data.api_status === "OK") {
        applyThread(data);
      } else {
        myToast.notify_warning(data.api_remark || "Could not send message.");
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning(
        error?.response?.status === 404
          ? "Support chat API is missing on the server. Deploy the PHP files."
          : "The assistant could not be reached. Try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const handoff = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await requestSupportHuman(chat?.chat_id);
      if (data.api_status === "OK") {
        applyThread(data);
        myToast.notify_success("Support has been notified.");
      } else {
        myToast.notify_warning(data.api_remark || "Could not request support.");
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Could not request support.");
    } finally {
      setBusy(false);
    }
  };

  const closeChat = async () => {
    if (busy || !chat?.chat_id) return;
    setBusy(true);
    try {
      const data = await closeSupportChat(chat.chat_id);
      if (data.api_status === "OK") {
        applyThread(data);
      } else {
        myToast.notify_warning(data.api_remark || "Could not close chat.");
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Could not close chat.");
    } finally {
      setBusy(false);
    }
  };

  const banner = !serviceOn
    ? "Support chat is currently turned off."
    : mode === "HUMAN"
      ? chat?.assigned_admin_name
        ? `Talking with ${chat.assigned_admin_name}. AI is paused.`
        : "Human support — send your question and our team will reply."
      : mode === "CLOSED"
        ? "This chat is closed."
        : "AI mode — answers use your school data.";

  if (!serviceOn) {
    return null;
  }

  return (
    <div style={{ fontFamily: 'Inter, system-ui, "Segoe UI", Roboto, Arial, sans-serif' }}>
      {open ? (
        <section
          aria-label="School assistant"
          style={{
            position: "fixed",
            right: 20,
            bottom: 84,
            width: "min(400px, calc(100vw - 24px))",
            height: 520,
            maxHeight: "calc(100vh - 120px)",
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 16px 40px rgba(1, 87, 155, 0.22)",
            zIndex: 1400,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            border: "1px solid #E1F5FE",
          }}
        >
          <header
            style={{
              background: COLORS.primary_color_shade7,
              color: "#fff",
              padding: "12px 14px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <FaRobot aria-hidden />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>School AI assistant</div>
              <div style={{ fontSize: 11, opacity: 0.9 }}>Saved chat · CRM can join</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close assistant"
              style={{ border: 0, background: "transparent", color: "#fff", cursor: "pointer", fontSize: 16 }}
            >
              <FaTimes />
            </button>
          </header>

          <div
            style={{
              padding: "8px 12px",
              fontSize: 11,
              background: mode === "HUMAN" ? "#FFF8E1" : mode === "CLOSED" ? "#FFEBEE" : "#E1F5FE",
              color: "#37474F",
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span style={{ flex: 1 }}>{banner}</span>
            <Link to="/AdminMain/SupportChat" style={{ color: COLORS.primary_color_shade9, fontWeight: 600 }}>
              Open page
            </Link>
          </div>

          <div ref={listRef} style={{ flex: 1, overflowY: "auto", padding: 12, background: "#F7FBFE" }}>
            {visible.length === 0 ? (
              <div>
                <p style={{ margin: "0 0 10px", color: "#546E7A", fontSize: 13, lineHeight: 1.5 }}>
                  Ask about fees, students, classes, exams, or today. Use Talk to support for a human.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {SUGGESTIONS.map((item) => (
                    <button
                      key={item}
                      type="button"
                      disabled={busy}
                      onClick={() => ask(item)}
                      style={{
                        border: "1px solid #B3E5FC",
                        background: "#fff",
                        color: COLORS.primary_color_shade9,
                        borderRadius: 999,
                        padding: "5px 10px",
                        fontSize: 11,
                        cursor: busy ? "default" : "pointer",
                      }}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              visible.map((message) => {
                const role = message.sender_role || "ai";
                const style = bubbleColor(role);
                const mine = role === "school";
                return (
                  <div
                    key={message.message_id || `${role}-${message.date_added}`}
                    style={{
                      marginBottom: 8,
                      display: "flex",
                      justifyContent: mine ? "flex-end" : "flex-start",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "85%",
                        padding: "8px 10px",
                        borderRadius: 12,
                        fontSize: 13,
                        lineHeight: 1.45,
                        whiteSpace: "pre-wrap",
                        background: style.bg,
                        color: style.color,
                        border: style.border,
                      }}
                    >
                      {!mine ? (
                        <div style={{ fontSize: 10, opacity: 0.8, marginBottom: 2 }}>
                          {role === "crm" ? message.sender_name || "Support" : "School AI"}
                        </div>
                      ) : null}
                      {message.message_text}
                    </div>
                  </div>
                );
              })
            )}
            {busy ? <div style={{ fontSize: 12, color: "#0288D1" }}>Working…</div> : null}
          </div>

          <div style={{ display: "flex", gap: 6, padding: "6px 10px", borderTop: "1px solid #E1F5FE" }}>
            {settings.ai_enabled ? (
              <button
                type="button"
                disabled={busy || mode === "HUMAN" || mode === "CLOSED"}
                onClick={handoff}
                style={{
                  border: "1px solid #B3E5FC",
                  background: "#fff",
                  borderRadius: 8,
                  padding: "6px 8px",
                  fontSize: 11,
                  cursor: "pointer",
                  color: COLORS.primary_color_shade9,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <FaHeadset /> Talk to support
              </button>
            ) : null}
            <button
              type="button"
              disabled={busy || mode === "CLOSED" || !chat?.chat_id}
              onClick={closeChat}
              style={{
                border: "1px solid #FFCDD2",
                background: "#fff",
                borderRadius: 8,
                padding: "6px 8px",
                fontSize: 11,
                cursor: "pointer",
                color: "#C62828",
              }}
            >
              Close chat
            </button>
          </div>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              ask();
            }}
            style={{ display: "flex", gap: 8, padding: 10, borderTop: "1px solid #E1F5FE" }}
          >
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={mode === "HUMAN" ? "Message support…" : "Ask about fees, students…"}
              aria-label="Message"
              disabled={busy}
              style={{
                flex: 1,
                border: "1px solid #B3E5FC",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 13,
                outline: "none",
              }}
            />
            <button
              type="submit"
              disabled={busy || !draft.trim()}
              aria-label="Send message"
              style={{
                border: 0,
                borderRadius: 10,
                background: COLORS.primary_color_shade7,
                color: "#fff",
                width: 40,
                cursor: busy || !draft.trim() ? "default" : "pointer",
                opacity: busy || !draft.trim() ? 0.6 : 1,
              }}
            >
              <FaPaperPlane />
            </button>
          </form>
        </section>
      ) : null}
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={open ? "Hide school assistant" : "Open school assistant"}
        style={{
          position: "fixed",
          right: 20,
          bottom: 20,
          zIndex: 1400,
          width: 52,
          height: 52,
          borderRadius: "50%",
          border: 0,
          background: COLORS.primary_color_shade9,
          color: "#fff",
          boxShadow: "0 8px 20px rgba(1, 87, 155, 0.35)",
          cursor: "pointer",
          fontSize: 20,
        }}
      >
        {open ? <FaTimes /> : <FaRobot />}
      </button>
    </div>
  );
}
